import type { ReglaReserva, ValoresRegla } from '../../api/ops/disponibilidad';

/**
 * Lógica de «Cuándo se puede reservar» (3.3, componentes.md §5f): los valores de la regla con los de por defecto de G256,
 * sus opciones con «antes» dentro y las frases llanas («Desde 60 días antes hasta 30 min antes»). Nunca «antelación
 * mínima», «horizonte» ni «rejilla».
 */

/** Valores por defecto de la regla del negocio (G256). */
/** G368: la rejilla de 15 minutos es la de por defecto. */
export const POR_DEFECTO = { min: 60, mismoDia: 'until_slot' as const, max: 90, paso: 15 };

export const MIN_OPC: readonly [number, string][] = [[0, 'En cualquier momento'], [15, '15 minutos antes'], [30, '30 minutos antes'], [60, '1 hora antes'], [120, '2 horas antes'], [240, '4 horas antes'], [720, '12 horas antes'], [1440, '1 día antes'], [2880, '2 días antes'], [10080, '1 semana antes']];
export const CORTE_OPC: readonly [number, string][] = [[15, '15 minutos antes'], [30, '30 minutos antes'], [60, '1 hora antes'], [120, '2 horas antes'], [240, '4 horas antes']];
export const MAX_OPC: readonly [number, string][] = [[7, '7 días antes'], [14, '14 días antes'], [30, '30 días antes'], [60, '60 días antes'], [90, '90 días antes'], [180, '180 días antes'], [365, '1 año antes'], [730, '2 años antes']];
export const PASO_OPC: readonly [number, string][] = [[5, 'Cada 5 minutos'], [10, 'Cada 10 minutos'], [15, 'Cada 15 minutos'], [20, 'Cada 20 minutos'], [30, 'Cada 30 minutos'], [60, 'Cada hora']];
/** Tipos de lo que tiene regla propia que se enseñan aquí (G252: no los profesionales). */
export const TIPOS: Readonly<Record<string, string>> = { zone: 'Zona', resource: 'Recurso', resource_union: 'Unión' };

export type MismoDia = NonNullable<ValoresRegla['same_day_mode']>;

export interface Valores {
    min: number;
    mismoDia: MismoDia;
    corte: number;
    max: number;
    paso: number | null;
}

export function valoresDe(r: ValoresRegla | null | undefined): Valores {
    return {
        min: r?.min_advance_minutes ?? POR_DEFECTO.min,
        mismoDia: r?.same_day_mode ?? POR_DEFECTO.mismoDia,
        corte: r?.same_day_cutoff_minutes ?? 30,
        max: r?.max_advance_days ?? POR_DEFECTO.max,
        paso: r?.slot_step_minutes ?? POR_DEFECTO.paso,
    };
}

/** «60 días», «1 año». */
export const textoMax = (d: number) => (d === 365 ? '1 año' : d === 730 ? '2 años' : `${String(d)} días`);
/** «30 minutos», «2 horas», «1 día», «1 semana». */
export function textoMin(m: number): string {
    if (m >= 10080 && m % 10080 === 0) {
        return m === 10080 ? '1 semana' : `${String(m / 10080)} semanas`;
    }
    if (m >= 1440 && m % 1440 === 0) {
        return m === 1440 ? '1 día' : `${String(m / 1440)} días`;
    }
    if (m >= 60 && m % 60 === 0) {
        return m === 60 ? '1 hora' : `${String(m / 60)} horas`;
    }
    return `${String(m)} minutos`;
}
/** Corto, para la tarjeta: «30 min», «2 h», «1 día». */
function textoMinCorto(m: number): string {
    if (m >= 1440) {
        return textoMin(m);
    }
    return m >= 60 && m % 60 === 0 ? `${String(m / 60)} h` : `${String(m)} min`;
}

/** Resumen de la tarjeta: «Desde 60 días antes hasta 30 min antes». */
export function resumenRegla(r: ValoresRegla | null | undefined): string {
    const v = valoresDe(r);
    return `Desde ${textoMax(v.max)} antes hasta ${v.min === 0 ? 'el último momento' : `${textoMinCorto(v.min)} antes`}`;
}

export const conActual = (lista: readonly [number, string][], actual: number, texto: (n: number) => string) => (lista.some(([v]) => v === actual) ? lista : [...lista, [actual, texto(actual)] as [number, string]].sort((a, b) => a[0] - b[0]));

export function diferencias(r: ReglaReserva, palabraCita: boolean): string {
    const partes: string[] = [];
    const max = r.max_advance_days;
    const min = r.min_advance_minutes;
    if (max != null && min != null) {
        partes.push(`desde ${textoMax(max)} hasta ${min === 0 ? 'el último momento' : `${textoMin(min)} antes`}`);
    } else if (max != null) {
        partes.push(`como pronto ${textoMax(max)} antes`);
    } else if (min != null) {
        partes.push(min === 0 ? 'como tarde, en cualquier momento' : `como tarde ${textoMin(min)} antes`);
    }
    if (r.same_day_mode === 'until_opening') {
        partes.push('el mismo día, solo antes de abrir');
    } else if (r.same_day_mode === 'until_slot') {
        partes.push(`el mismo día, hasta la hora de ${palabraCita ? 'la cita' : 'la reserva'}`);
    } else if (r.same_day_mode === 'minutes_before') {
        partes.push(`el mismo día, hasta ${textoMin(r.same_day_cutoff_minutes ?? 30)} antes`);
    }
    if (r.slot_step_minutes != null) {
        partes.push(`cada ${textoMin(r.slot_step_minutes)}`);
    }
    return partes.join(' · ');
}

